import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Save, ListPlus } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const CustomFieldsSection = ({ entityType, entityId }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/custom-fields/values/${entityType}/${entityId}`);
      setFields(res.data.fields || []);
      const v = {};
      (res.data.fields || []).forEach((f) => { v[f.id] = f.value ?? ''; });
      setValues(v);
    } catch (e) {
      console.error('custom fields load error', e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(values).forEach(([k, v]) => { payload[k] = v === '' ? null : String(v); });
      await api.put(`/custom-fields/values/${entityType}/${entityId}`, { values: payload });
      toast.success('Zusatzfelder gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (f) => {
    const val = values[f.id] ?? '';
    const set = (v) => setValues((p) => ({ ...p, [f.id]: v }));
    switch (f.field_type) {
      case 'checkbox':
        return (
          <Switch
            checked={val === 'true'}
            onCheckedChange={(c) => set(c ? 'true' : 'false')}
            data-testid={`custom-field-input-${f.id}`}
          />
        );
      case 'select':
        return (
          <Select value={val || undefined} onValueChange={set}>
            <SelectTrigger className="w-full" data-testid={`custom-field-input-${f.id}`}>
              <SelectValue placeholder="Bitte wählen..." />
            </SelectTrigger>
            <SelectContent>
              {(f.options || []).map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'number':
        return <Input type="number" value={val} onChange={(e) => set(e.target.value)} data-testid={`custom-field-input-${f.id}`} />;
      case 'date':
        return <Input type="date" value={val} onChange={(e) => set(e.target.value)} data-testid={`custom-field-input-${f.id}`} />;
      default:
        return <Input value={val} onChange={(e) => set(e.target.value)} data-testid={`custom-field-input-${f.id}`} />;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  if (fields.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-4 flex items-center gap-2" data-testid="custom-fields-empty">
        <ListPlus className="w-4 h-4" />
        Keine Zusatzfelder definiert. Admins können sie unter Einstellungen → Zusatzfelder anlegen.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="custom-fields-section">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.id} className={f.field_type === 'checkbox' ? 'flex items-center justify-between p-3 bg-slate-50 rounded-lg' : ''}>
            <Label className="text-sm text-slate-700 mb-1 block">{f.label}</Label>
            {renderInput(f)}
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} data-testid="custom-fields-save-btn"
                className="bg-slate-900 text-white hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </div>
    </div>
  );
};
